import type { FormEvent } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { fetchUserSettings, getDefaultSettings, saveUserSettings } from '../services/settingsRepo'
import { ensureSubscription, fetchUserPlan } from '../services/subscriptionRepo'
import { fetchApplications, insertApplication, updateApplication, updateLastActive, deleteApplicationDb } from '../services/supabaseRepo'
import { extractJobFromUrl } from '../services/extractJob'
import type {
  Application,
  ApplicationStatus,
  Currency,
  ExtractedPosting,
  RejectReason,
  SalaryPeriod,
  SalaryRange,
  SankeyDatum,
  Subscription,
  SubscriptionPlan,
  UserSettings,
  WorkModeType,
} from '../types'
import { FREE_TIER_LIMITS } from '../types'
import { canAddApplication } from '../utils/subscription'
import { isValidTransition } from '../utils/statusMachine'
import { convertCurrency, CURRENCY_SYMBOLS } from '../utils/currency'
import { hourlyToMonthly } from '../utils'
import { trackGAApplicationAdded, setGAUserProperties } from '../utils/googleAnalytics'
import { isRealApplication, realApplicationCount } from '../utils/activation'
import { trackApplicationAdded } from '../utils/facebookPixel'
import { generateEventId, getTrackingData } from '../utils/trackingCookies'
import { supabase } from '../services/supabaseClient'
import {
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import type { ComponentType } from 'react'

// Helper
const createId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

// Statuses where automatic status progression (based on stage completion) is disabled.
// This is different from statusMachine.ts terminal statuses - users can still manually
// change these statuses, but toggling stages won't auto-progress to offer/in_process.
const AUTO_PROGRESSION_DISABLED_STATUSES: ApplicationStatus[] = [
  'rejected',
  'hired',
  'archived',
  'ghosted',
  'withdrawn',
  'offer_declined',
  'accepted',
]

const STATUS_ORDER: ApplicationStatus[] = [
  'saved',
  'applied',
  'in_process',
  'offer',
  'accepted',
  'offer_declined',
  'ghosted',
  'withdrawn',
  'rejected',
  'hired',
  'archived',
]

type StatItem = {
  label: string
  value: string
  hint: string
  proOnly: boolean
  icon: ComponentType<{ className?: string }>
  valueTooltip?: string
}

type DepthStats = {
  distribution: { depth: number; count: number }[]
  avgDepth: number
  maxDepth: number
  maxDepthCompany: string
  total: number
  completionDistribution: { bucket: string; count: number }[]
  avgCompletion: number
  fullyCompleted: number
  appsWithStages: number
}

type Metrics = {
  conversions: { offer: number; hire: number }
  avgDays: number | null
  salaryBox: { min: number | null; median: number | null; max: number | null }
  weeklyTrend: [string, number][]
}

type Stats = {
  total: number
  interviewRate: number
  avg: number | null
  responseRate: number
  positiveResponseRate: number
  medianDaysToFirstResponse: number | null
  activeThisMonth: number
}

export type ApplicationsContextValue = {
  // Data
  applications: Application[]
  settings: UserSettings
  subscription: Subscription | null
  userPlan: SubscriptionPlan

  // Loading states
  isLoadingApps: boolean
  appsLoadError: string | null
  isLoadingSettings: boolean
  isSavingSettings: boolean
  settingsError: string | null

  // Computed
  activeApps: Application[]
  archivedApps: Application[]
  effectivePlan: 'free' | 'pro'
  isPro: boolean

  // Dashboard UI state
  viewMode: 'active' | 'archived'
  companyFilter: string
  displayMode: 'cards' | 'table'
  sortBy: 'created_desc' | 'created_asc' | 'company_asc' | 'company_desc'
  sortKey: 'company' | 'status' | 'salary' | 'stages' | 'createdAt' | 'updatedAt'
  sortDir: 'asc' | 'desc'
  statusFilter: 'all' | ApplicationStatus
  appsPage: number
  totalPages: number

  // Computed for dashboard
  visibleApps: Application[]
  filteredApps: Application[]
  paginatedApps: Application[]
  stats: Stats
  metrics: Metrics
  sankeyData: SankeyDatum
  depthStats: DepthStats
  statItems: StatItem[]
  analyticsStatItems: StatItem[]
  statusLabels: Record<ApplicationStatus, string>
  availableStatusFilters: ApplicationStatus[]
  displayCurrency: string
  currencySymbol: string

  // Form state
  formUrl: string
  isSubmitting: boolean
  formError: string | null
  extractedData: ExtractedPosting | null
  isEditModalOpen: boolean
  isSavingApp: boolean

  // Reject flow
  openRejectId: string | null
  rejectState: RejectReason

  // Actions - Applications
  handleAddApplication: (e: FormEvent, quickAdd?: boolean) => Promise<void>
  handleManualAdd: () => void
  handleConfirmExtractedData: (data: Partial<Application> & {
    targetAmount?: number | null
    targetPeriod?: SalaryPeriod
    targetCurrency?: Currency
  }) => Promise<void>
  toggleStage: (appId: string, stageId: string) => void
  updateStages: (appId: string, stages: Application['processStages']) => Promise<void>
  updateStatus: (appId: string, status: ApplicationStatus, offerReason?: string | null) => void
  rejectApplication: (appId: string, reason: RejectReason) => void
  archiveApplication: (appId: string) => void
  unarchiveApplication: (appId: string) => void
  updateNote: (appId: string, note: string) => void
  updateDetails: (appId: string, details: { positionTitle?: string | null; contractType?: string | null; benefits?: string | null; techStack?: string[]; workMode?: WorkModeType; officeDays?: number | null }) => void
  toggleFavorite: (appId: string) => void
  updateCompensation: (appId: string, payload: {
    salaryRange: SalaryRange
    targetAmount: number | null
    targetPeriod: SalaryPeriod
    targetCurrency: Currency
  }) => Promise<void>
  checkDuplicate: (url: string) => Application | undefined

  // Actions - Settings
  handleSaveSettings: (newSettings: UserSettings) => Promise<void>
  refreshSubscription: () => Promise<void>

  // Actions - UI
  setViewMode: (mode: 'active' | 'archived') => void
  setCompanyFilter: (filter: string) => void
  setDisplayMode: (mode: 'cards' | 'table') => void
  setSortKey: (key: 'company' | 'status' | 'salary' | 'stages' | 'createdAt' | 'updatedAt') => void
  setSortDir: (dir: 'asc' | 'desc') => void
  setStatusFilter: (status: 'all' | ApplicationStatus) => void
  setAppsPage: (page: number) => void
  setFormUrl: (url: string) => void
  setOpenRejectId: (id: string | null) => void
  setRejectState: (state: RejectReason) => void
  handleCancelDraft: () => Promise<void>
  handleDisplayModeChange: (mode: 'cards' | 'table') => void
}

export const ApplicationsContext = createContext<ApplicationsContextValue | null>(null)

type ApplicationsProviderProps = {
  children: ReactNode
}

export function ApplicationsProvider({ children }: ApplicationsProviderProps) {
  const { sessionUserId } = useAuth()

  // Data state
  const [applications, setApplications] = useState<Application[]>([])
  const [settings, setSettings] = useState<UserSettings>(getDefaultSettings())
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [userPlan, setUserPlan] = useState<SubscriptionPlan>('free')

  // Loading states
  const [isLoadingApps, setIsLoadingApps] = useState(false)
  const [appsLoadError, setAppsLoadError] = useState<string | null>(null)
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  // UI state
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active')
  const [companyFilter, setCompanyFilter] = useState('')
  const [displayMode, setDisplayMode] = useState<'cards' | 'table'>(window.innerWidth < 768 ? 'cards' : 'table')
  const [sortBy] = useState<'created_desc' | 'created_asc' | 'company_asc' | 'company_desc'>('created_desc')
  const [sortKey, setSortKey] = useState<'company' | 'status' | 'salary' | 'stages' | 'createdAt' | 'updatedAt'>('createdAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [statusFilter, setStatusFilter] = useState<'all' | ApplicationStatus>('all')
  const [appsPage, setAppsPage] = useState(1)

  // Form state
  const [formUrl, setFormUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [extractedData, setExtractedData] = useState<ExtractedPosting | null>(null)
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isSavingApp, setIsSavingApp] = useState(false)

  // Reject flow
  const [openRejectId, setOpenRejectId] = useState<string | null>(null)
  const [rejectState, setRejectState] = useState<RejectReason>({ code: 'too_expensive' })

  // Refs for deduplication
  const loadedAppsForUserRef = useRef<string | null>(null)
  const loadedSettingsForUserRef = useRef<string | null>(null)

  // Apply theme based on settings
  useTheme(settings.themeMode)

  // Static data
  const statusLabels: Record<ApplicationStatus, string> = {
    saved: 'Saved',
    applied: 'Applied',
    in_process: 'In progress',
    offer: 'Offer',
    accepted: 'Offer accepted',
    offer_declined: 'Offer declined',
    ghosted: 'Ghosted',
    withdrawn: 'Withdrawn',
    rejected: 'Rejected',
    hired: 'Hired',
    archived: 'Archived',
  }

  const availableStatusFilters = STATUS_ORDER.filter((s) => s !== 'archived')

  // Computed values
  const activeApps = useMemo(() => applications.filter((a) => !a.archived), [applications])
  const archivedApps = useMemo(() => applications.filter((a) => a.archived), [applications])
  const visibleApps = viewMode === 'active' ? activeApps : archivedApps

  const sortedApps = useMemo(() => {
    const next = [...visibleApps]
    next.sort((a, b) => {
      switch (sortBy) {
        case 'created_asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'company_asc':
          return a.company.localeCompare(b.company, 'pl')
        case 'company_desc':
          return b.company.localeCompare(a.company, 'pl')
        case 'created_desc':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
    })
    return next
  }, [visibleApps, sortBy])

  const filteredApps = useMemo(() => {
    const term = companyFilter.trim().toLowerCase()
    return sortedApps.filter((app) => {
      const matchesCompany = !term || app.company.toLowerCase().includes(term)
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter
      return matchesCompany && matchesStatus
    })
  }, [sortedApps, companyFilter, statusFilter])

  const PAGE_SIZE = displayMode === 'cards' ? 12 : 10
  const totalPages = Math.max(1, Math.ceil(filteredApps.length / PAGE_SIZE))
  const safePage = Math.min(appsPage, totalPages)

  const paginatedApps = useMemo(
    () => filteredApps.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredApps, safePage, PAGE_SIZE]
  )

  // Stats computation
  const stats = useMemo<Stats>(() => {
    const total = activeApps.filter((a) => a.status !== 'hired' && a.status !== 'withdrawn' && a.status !== 'rejected').length
    const responded = activeApps.filter((a) => a.processStages.some((s) => s.completed)).length
    const responseRate = total ? Math.round((responded / total) * 100) : 0

    const progressedStatuses = new Set<ApplicationStatus>([
      'in_process',
      'offer',
      'accepted',
      'offer_declined',
      'ghosted',
      'withdrawn',
      'rejected',
      'hired',
    ])
    const appliedCount = activeApps.filter((a) => a.status !== 'saved').length
    const positiveResponses = activeApps.filter((a) => progressedStatuses.has(a.status)).length
    const positiveResponseRate = appliedCount ? Math.round((positiveResponses / appliedCount) * 100) : 0

    const firstResponseDurations = activeApps
      .filter((a) => a.status !== 'saved' && (a.processStages.some((s) => s.completed) || progressedStatuses.has(a.status)))
      .map((a) => Math.max(0, Math.floor((new Date(a.updatedAt).getTime() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60 * 24))))
      .sort((a, b) => a - b)
    const medianDaysToFirstResponse = firstResponseDurations.length
      ? (firstResponseDurations.length % 2 === 0
        ? Math.round((firstResponseDurations[firstResponseDurations.length / 2 - 1] + firstResponseDurations[firstResponseDurations.length / 2]) / 2)
        : firstResponseDurations[Math.floor(firstResponseDurations.length / 2)])
      : null
    
    // Interview rate: % of applications that reached interview stage
    const interviewed = activeApps.filter((a) => 
      a.processStages.some((s) => s.completed && (s.name.toLowerCase().includes('interview') || s.name.toLowerCase().includes('rozmowa')))
    ).length
    const interviewRate = total ? Math.round((interviewed / total) * 100) : 0
    
    // Active this month: applications created in current month
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const activeThisMonth = activeApps.filter((a) => new Date(a.createdAt) >= startOfMonth).length
    const { currencyConversionEnabled, targetCurrency } = settings
    const shouldConvert = currencyConversionEnabled && targetCurrency !== 'original'
    const expectations = activeApps
      .filter((a) => a.expectedSalary != null)
      .map((a) => {
        const converted = shouldConvert
          ? convertCurrency(a.expectedSalary!, 'PLN', targetCurrency as Exclude<typeof targetCurrency, 'original'>)
          : a.expectedSalary!
        return converted
      })
      .filter((val): val is number => val != null)
    const avg = expectations.length
      ? Math.round(expectations.reduce((acc, val) => acc + val, 0) / expectations.length)
      : null
    return { total, interviewRate, avg, responseRate, positiveResponseRate, medianDaysToFirstResponse, activeThisMonth }
  }, [activeApps, applications, settings])

  // Metrics computation
  const metrics = useMemo<Metrics>(() => {
    const total = applications.length
    const offerStatuses: ApplicationStatus[] = ['offer', 'offer_declined', 'accepted']
    const hireStatuses: ApplicationStatus[] = ['hired', 'accepted']
    const outcomeStatuses: ApplicationStatus[] = [
      'rejected', 'offer', 'offer_declined', 'accepted', 'hired', 'ghosted', 'withdrawn',
    ]

    const offers = applications.filter((a) => offerStatuses.includes(a.status)).length
    const hires = applications.filter((a) => hireStatuses.includes(a.status)).length
    const conversions = {
      offer: total ? Math.round((offers / total) * 100) : 0,
      hire: total ? Math.round((hires / total) * 100) : 0,
    }

    const durations = applications
      .filter((a) => outcomeStatuses.includes(a.status))
      .map((a) =>
        Math.max(0, Math.floor((new Date(a.updatedAt).getTime() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60 * 24)))
      )
    const avgDays = durations.length ? Math.round(durations.reduce((acc, v) => acc + v, 0) / durations.length) : null

    const { currencyConversionEnabled, targetCurrency } = settings
    const shouldConvert = currencyConversionEnabled && targetCurrency !== 'original'
    const salaries: number[] = []
    applications.forEach((a) => {
      if (a.salaryRange) {
        const min = shouldConvert
          ? convertCurrency(a.salaryRange.min, a.salaryRange.currency, targetCurrency as Exclude<typeof targetCurrency, 'original'>)
          : a.salaryRange.min
        const max = shouldConvert
          ? convertCurrency(a.salaryRange.max, a.salaryRange.currency, targetCurrency as Exclude<typeof targetCurrency, 'original'>)
          : a.salaryRange.max
        if (min != null) salaries.push(min)
        if (max != null) salaries.push(max)
      }
      if (a.expectedSalary != null) {
        const converted = shouldConvert
          ? convertCurrency(a.expectedSalary, 'PLN', targetCurrency as Exclude<typeof targetCurrency, 'original'>)
          : a.expectedSalary
        if (converted != null) salaries.push(converted)
      } else if (a.expectedHourly != null) {
        const monthly = hourlyToMonthly(a.expectedHourly) ?? 0
        const converted = shouldConvert
          ? convertCurrency(monthly, 'PLN', targetCurrency as Exclude<typeof targetCurrency, 'original'>)
          : monthly
        if (converted != null) salaries.push(converted)
      }
    })
    const sorted = salaries.filter(Number.isFinite).sort((a, b) => a - b)
    const pickMedian = (arr: number[]) => {
      if (!arr.length) return null
      const mid = Math.floor(arr.length / 2)
      return arr.length % 2 === 0 ? Math.round((arr[mid - 1] + arr[mid]) / 2) : arr[mid]
    }
    const salaryBox = {
      min: sorted[0] ?? null,
      median: pickMedian(sorted),
      max: sorted[sorted.length - 1] ?? null,
    }

    const isoWeek = (date: Date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
      return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`
    }

    const trendMap = new Map<string, number>()
    applications.forEach((a) => {
      const key = isoWeek(new Date(a.createdAt))
      trendMap.set(key, (trendMap.get(key) ?? 0) + 1)
    })

    const sortedTrend = Array.from(trendMap.entries())
      .sort((a, b) => (a[0] > b[0] ? -1 : 1))
      .slice(0, 6)

    return { conversions, avgDays, salaryBox, weeklyTrend: sortedTrend }
  }, [applications, settings])

  // Sankey data
  const sankeyData = useMemo<SankeyDatum>(() => {
    const linksMap: Record<string, number> = {}
    const nodesSet = new Set<string>(['Applied'])

    const palette: Record<string, string> = {
      Applied: '#38bdf8',
      'In progress': '#94a3b8',
      Offer: '#22c55e',
      'Offer accepted': '#a855f7',
      'Offer declined': '#f59e0b',
      Hired: '#10b981',
      Ghosted: '#6b7280',
      Withdrawn: '#78716c',
      Rejected: '#e11d48',
    }

    const addLink = (from: string, to: string) => {
      const key = `${from}->${to}`
      linksMap[key] = (linksMap[key] ?? 0) + 1
      nodesSet.add(from)
      nodesSet.add(to)
    }

    const statusToNode = (status: string): string => {
      switch (status) {
        case 'applied': return 'Applied'
        case 'in_process': return 'In progress'
        case 'offer': return 'Offer'
        case 'accepted': return 'Offer accepted'
        case 'offer_declined': return 'Offer declined'
        case 'hired': return 'Hired'
        case 'ghosted': return 'Ghosted'
        case 'withdrawn': return 'Withdrawn'
        case 'rejected': return 'Rejected'
        default: return status
      }
    }

    activeApps.forEach((app) => {
      const currentNode = statusToNode(app.status)
      if (currentNode === 'Applied') {
        nodesSet.add('Applied')
      } else if (currentNode === 'In progress') {
        addLink('Applied', 'In progress')
      } else if (currentNode === 'Offer') {
        addLink('Applied', 'In progress')
        addLink('In progress', 'Offer')
      } else if (currentNode === 'Offer accepted') {
        addLink('Applied', 'In progress')
        addLink('In progress', 'Offer')
        addLink('Offer', 'Offer accepted')
      } else if (currentNode === 'Offer declined') {
        addLink('Applied', 'In progress')
        addLink('In progress', 'Offer')
        addLink('Offer', 'Offer declined')
      } else if (currentNode === 'Hired') {
        addLink('Applied', 'In progress')
        addLink('In progress', 'Offer')
        addLink('Offer', 'Offer accepted')
        addLink('Offer accepted', 'Hired')
      } else if (currentNode === 'Ghosted') {
        addLink('Applied', 'Ghosted')
      } else if (currentNode === 'Withdrawn') {
        addLink('Applied', 'Withdrawn')
      } else if (currentNode === 'Rejected') {
        addLink('Applied', 'Rejected')
      }
    })

    const nodeOrder = [
      'Applied', 'In progress', 'Offer', 'Offer accepted', 'Hired',
      'Offer declined', 'Ghosted', 'Withdrawn', 'Rejected'
    ]
    const nodeNames = nodeOrder.filter((name) => nodesSet.has(name))
    const nodeIndex = new Map(nodeNames.map((name, idx) => [name, idx]))

    const nodes = nodeNames.map((name) => {
      const color = palette[name] ?? '#94a3b8'
      return { name, color, fill: color }
    })

    const links = Object.entries(linksMap).map(([key, value]) => {
      const [sourceName, targetName] = key.split('->')
      return {
        source: nodeIndex.get(sourceName) ?? 0,
        target: nodeIndex.get(targetName) ?? 0,
        value,
      }
    })

    return { nodes, links }
  }, [activeApps])

  // Depth stats
  const depthStats = useMemo<DepthStats>(() => {
    const depthCounts: Record<number, number> = {}
    const completionBuckets = { '0': 0, '1-25': 0, '26-50': 0, '51-75': 0, '76-99': 0, '100': 0 }
    let totalDepth = 0
    let totalCompletion = 0
    let maxDepth = 0
    let maxDepthCompany = ''
    let fullyCompleted = 0
    let appsWithStages = 0

    activeApps.forEach((app) => {
      const totalStages = app.processStages.length
      let completed = 0
      for (const stage of app.processStages) {
        if (stage.completed) completed++
        else break
      }

      depthCounts[completed] = (depthCounts[completed] ?? 0) + 1
      totalDepth += completed

      if (completed > maxDepth) {
        maxDepth = completed
        maxDepthCompany = app.company
      }

      if (totalStages > 0) {
        appsWithStages++
        const completionPct = (completed / totalStages) * 100
        totalCompletion += completionPct

        if (completionPct === 0) completionBuckets['0']++
        else if (completionPct <= 25) completionBuckets['1-25']++
        else if (completionPct <= 50) completionBuckets['26-50']++
        else if (completionPct <= 75) completionBuckets['51-75']++
        else if (completionPct < 100) completionBuckets['76-99']++
        else {
          completionBuckets['100']++
          fullyCompleted++
        }
      }
    })

    const avgDepth = activeApps.length > 0 ? totalDepth / activeApps.length : 0
    const avgCompletion = appsWithStages > 0 ? totalCompletion / appsWithStages : 0

    const distribution = Object.entries(depthCounts)
      .map(([depth, count]) => ({ depth: Number(depth), count }))
      .sort((a, b) => a.depth - b.depth)

    const completionDistribution = [
      { bucket: '0%', count: completionBuckets['0'] },
      { bucket: '1-25%', count: completionBuckets['1-25'] },
      { bucket: '26-50%', count: completionBuckets['26-50'] },
      { bucket: '51-75%', count: completionBuckets['51-75'] },
      { bucket: '76-99%', count: completionBuckets['76-99'] },
      { bucket: '100%', count: completionBuckets['100'] },
    ].filter((b) => b.count > 0)

    return {
      distribution,
      avgDepth: Math.round(avgDepth * 10) / 10,
      maxDepth,
      maxDepthCompany,
      total: activeApps.length,
      completionDistribution,
      avgCompletion: Math.round(avgCompletion),
      fullyCompleted,
      appsWithStages,
    }
  }, [activeApps])

  const effectivePlan = userPlan
  const isPro = effectivePlan === 'pro'
  const displayCurrency = settings.currencyConversionEnabled ? settings.targetCurrency : 'PLN'
  const currencySymbol = CURRENCY_SYMBOLS[displayCurrency] || displayCurrency

  const statItems: StatItem[] = [
    { label: 'Active applications', value: stats.total.toString(), hint: 'All except Hired, Withdrawn, and Rejected', proOnly: false, icon: BriefcaseIcon },
    { label: 'Active this month', value: stats.activeThisMonth.toString(), hint: 'Applications created in the current month', proOnly: false, icon: BriefcaseIcon },
    { label: 'Response rate', value: `${stats.responseRate}%`, hint: 'Applications with at least one completed stage', proOnly: false, icon: ChatBubbleLeftRightIcon },
    { label: 'Offer conversion', value: `${metrics.conversions.offer}%`, hint: 'Share of applications with offer/declined/accepted status', proOnly: false, icon: TrophyIcon },
    { label: 'Avg. time to outcome', value: metrics.avgDays != null ? `${metrics.avgDays} days` : 'N/D', hint: 'From submission to terminal status', proOnly: true, icon: ClockIcon },
    { label: 'Interview rate', value: `${stats.interviewRate}%`, hint: 'Applications that reached interview stage', proOnly: true, icon: ChatBubbleLeftRightIcon },
  ]

  const analyticsStatItems: StatItem[] = [
    ...statItems,
    { label: 'Positive response rate', value: `${stats.positiveResponseRate}%`, hint: 'Applications that reached in-process or later (excluding saved)', proOnly: true, icon: ChatBubbleLeftRightIcon },
    { label: 'Median days to first response', value: stats.medianDaysToFirstResponse != null ? `${stats.medianDaysToFirstResponse} days` : 'N/D', hint: 'Based on first progress signal using created/updated timestamps', proOnly: true, icon: ClockIcon },
  ]

  // Load data when user changes
  useEffect(() => {
    const loadUserApps = async (userId: string) => {
      if (loadedAppsForUserRef.current === userId) return
      setIsLoadingApps(true)
      setAppsLoadError(null)
      try {
        const rows = await fetchApplications(userId)
        setApplications(rows)
        loadedAppsForUserRef.current = userId
        // Fire-and-forget: track user activity
        updateLastActive(userId)
      } catch (err) {
        console.error('fetchApplications failed', err)
        setApplications([])
        setAppsLoadError('Failed to load applications (timeout / no connection).')
        loadedAppsForUserRef.current = userId
      } finally {
        setIsLoadingApps(false)
      }
    }

    const loadSettings = async (userId: string) => {
      if (loadedSettingsForUserRef.current === userId) return
      setIsLoadingSettings(true)
      setSettingsError(null)
      try {
        const result = await fetchUserSettings(userId)
        setSettings(result)
        setDisplayMode(result.defaultDisplayMode)
        loadedSettingsForUserRef.current = userId
      } catch (err) {
        console.error('fetchUserSettings failed', err)
        setSettings(getDefaultSettings())
        setSettingsError('Failed to load settings.')
        loadedSettingsForUserRef.current = userId
      } finally {
        setIsLoadingSettings(false)
      }
    }

    const loadSubscription = async (userId: string) => {
      try {
        const sub = await ensureSubscription(userId)
        setSubscription(sub)
      } catch (err) {
        console.error('loadSubscription failed', err)
        setSubscription(null)
      }
    }

    const loadUserPlan = async (userId: string) => {
      try {
        const plan = await fetchUserPlan(userId)
        setUserPlan(plan)
      } catch (err) {
        console.error('loadUserPlan failed', err)
        setUserPlan('free')
      }
    }

    if (sessionUserId) {
      void loadUserApps(sessionUserId)
      void loadSettings(sessionUserId)
      void loadSubscription(sessionUserId)
      void loadUserPlan(sessionUserId)
    } else {
      // Reset on logout
      setApplications([])
      setIsLoadingApps(false)
      setAppsLoadError(null)
      setIsLoadingSettings(false)
      setSettings(getDefaultSettings())
      loadedAppsForUserRef.current = null
      loadedSettingsForUserRef.current = null
      setSubscription(null)
      setUserPlan('free')
    }
  }, [sessionUserId])

  // Set GA4 user properties after apps and plan are loaded
  useEffect(() => {
    if (sessionUserId && !isLoadingApps && userPlan) {
      setGAUserProperties(sessionUserId, userPlan, applications.length)
    }
  }, [sessionUserId, isLoadingApps, userPlan, applications.length])

  // Reset pagination on filter changes
  useEffect(() => {
    setAppsPage(1)
  }, [companyFilter, sortBy, statusFilter, viewMode, displayMode, filteredApps.length])

  // URL normalization
  const normalizeUrl = (url: string) => {
    try {
      const parsed = new URL(url.trim())
      let normalized = parsed.origin + parsed.pathname.replace(/\/$/, '')
      normalized = normalized.replace(/^(https?:\/\/)www\./, '$1')
      return normalized.toLowerCase()
    } catch {
      return url.trim().toLowerCase()
    }
  }

  const checkDuplicate = (url: string) => {
    if (!url.trim()) return undefined
    const normalized = normalizeUrl(url)
    return applications.find((app) => normalizeUrl(app.sourceUrl) === normalized)
  }

  // Refresh subscription and user plan
  const refreshSubscription = useCallback(async () => {
    if (!sessionUserId) return
    try {
      const sub = await ensureSubscription(sessionUserId)
      setSubscription(sub)
      
      // Also refresh the user plan from user_profile
      const plan = await fetchUserPlan(sessionUserId)
      setUserPlan(plan)
    } catch (err) {
      console.error('Failed to refresh subscription:', err)
    }
  }, [sessionUserId])

  // Create blank extraction for manual add
  const createBlankExtraction = (): ExtractedPosting => ({
    company: '',
    companyId: null,
    companyInfo: null,
    positionTitle: null,
    salaryRange: null,
    salaryPeriod: 'monthly',
    techStack: [],
    stages: [],
    contractType: null,
    benefits: null,
    workMode: null,
    officeDays: null,
    rawMarkdown: null,
  })

  // Add application handlers
  const handleAddApplication = async (e: FormEvent, quickAdd = false) => {
    e.preventDefault()
    setFormError(null)

    if (!sessionUserId) {
      setFormError('Sign in to add applications.')
      return
    }
    if (!formUrl.trim()) {
      setFormError('Provide a job posting link.')
      return
    }

    if (!canAddApplication(effectivePlan, applications.length)) {
      setFormError(`You've reached the limit of ${FREE_TIER_LIMITS.maxActiveApps} applications on the free plan. Upgrade to Pro for unlimited applications.`)
      return
    }

    const existingApp = checkDuplicate(formUrl)
    if (existingApp) {
      setFormError(`This URL is already tracked (${existingApp.company}). Check your applications list.`)
      return
    }

    setIsSubmitting(true)
    try {
      // For "Add & Review" (quickAdd = false), save as draft first
      const extraction = await extractJobFromUrl(formUrl.trim(), !quickAdd)
      setExtractedData(extraction)

      if (quickAdd) {
        await handleQuickAdd(extraction)
      } else {
        // Draft was saved by the edge function, track its ID
        if (extraction.draftId) {
          setCurrentDraftId(extraction.draftId)
        }
        setIsEditModalOpen(true)
      }
    } catch (err) {
      console.error(err)
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to extract data. Please try again.'
      setFormError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const trackSavedApplication = (saved: Application) => {
    if (!sessionUserId || !isRealApplication(saved)) return
      // This is called only after the real save or draft confirmation succeeds.
      const isFirst = realApplicationCount(applications) === 0
      const newCount = realApplicationCount(applications) + 1
      const eventId = isFirst ? generateEventId() : undefined
      trackGAApplicationAdded(isFirst, newCount)
      trackApplicationAdded(isFirst, newCount, eventId)

      // Fire server-side CAPI Lead event for deduplication
      if (isFirst && eventId) {
        const tracking = getTrackingData()
        supabase.functions.invoke('track-event', {
          body: { event_name: 'Lead', event_id: eventId, user_id: sessionUserId, jt_tracking: JSON.stringify(tracking), event_source_url: window.location.href },
        }).catch((err) => console.error('CAPI Lead error:', err))
      }

  }

  const handleQuickAdd = async (extraction: ExtractedPosting) => {
    if (!sessionUserId) return

    setIsSavingApp(true)
    try {
      const now = new Date().toISOString()
      const stages = extraction.stages.map((name) => ({
        id: createId(),
        name,
        completed: false,
      }))

      const newApp: Application = {
        id: createId(),
        company: extraction.company || 'N/D',
        companyId: extraction.companyId || null,
        companyInfo: extraction.companyInfo || null,
        positionTitle: extraction.positionTitle || null,
        sourceUrl: formUrl.trim(),
        expectedSalary: null,
        expectedHourly: null,
        targetAmount: null,
        targetPeriod: null,
        targetCurrency: 'PLN',
        salaryRange: extraction.salaryRange,
        techStack: extraction.techStack,
        contractType: extraction.contractType || null,
        benefits: extraction.benefits || null,
        workMode: extraction.workMode ?? null,
        officeDays: extraction.officeDays ?? null,
        processStages: stages,
        status: 'saved',
        archived: false,
        note: null,
        followUpAt: null,
        rawMarkdown: extraction.rawMarkdown || null,
        createdAt: now,
        updatedAt: now,
      }

      await insertApplication(sessionUserId, newApp)
      setApplications((prev) => [newApp, ...prev])

      trackSavedApplication(newApp)

      setFormUrl('')
      setExtractedData(null)
    } catch (err) {
      console.error(err)
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to save. Please try again.'
      setFormError(message)
    } finally {
      setIsSavingApp(false)
    }
  }

  const handleManualAdd = () => {
    if (!sessionUserId) {
      setFormError('Sign in to add applications.')
      return
    }

    if (!canAddApplication(effectivePlan, applications.length)) {
      setFormError(`You've reached the limit of ${FREE_TIER_LIMITS.maxActiveApps} applications on the free plan. Upgrade to Pro for unlimited applications.`)
      return
    }

    setExtractedData(createBlankExtraction())
    setIsEditModalOpen(true)
    setFormError(null)
  }

  const handleConfirmExtractedData = async (editedData: Partial<Application> & {
    targetAmount?: number | null
    targetPeriod?: SalaryPeriod
    targetCurrency?: Currency
  }) => {
    if (!sessionUserId || !extractedData) return

    setIsSavingApp(true)
    try {
      const now = new Date().toISOString()
      const stages = editedData.processStages || extractedData.stages.map((name) => ({
        id: createId(),
        name,
        completed: false,
      }))

      // If we have a draft ID, confirm the draft by updating it
      if (currentDraftId) {
        const updatedApp: Application = {
          id: currentDraftId,
          company: editedData.company || extractedData.company || 'N/D',
          companyId: extractedData.companyId || null,
          companyInfo: extractedData.companyInfo || null,
          positionTitle: editedData.positionTitle || extractedData.positionTitle || null,
          sourceUrl: formUrl.trim() || '',
          expectedSalary: null,
          expectedHourly: null,
          targetAmount: editedData.targetAmount ?? null,
          targetPeriod: editedData.targetPeriod ?? 'monthly',
          targetCurrency: editedData.targetCurrency ?? 'PLN',
          salaryRange: editedData.salaryRange || extractedData.salaryRange,
          techStack: editedData.techStack || extractedData.techStack,
          contractType: editedData.contractType || extractedData.contractType || null,
          benefits: editedData.benefits || extractedData.benefits || null,
          workMode: editedData.workMode ?? extractedData.workMode ?? null,
          officeDays: editedData.officeDays ?? extractedData.officeDays ?? null,
          processStages: stages,
          status: 'saved',
          archived: false,
          isDraft: false,
          note: null,
          followUpAt: null,
          rawMarkdown: extractedData.rawMarkdown || null,
          createdAt: now,
          updatedAt: now,
        }

        // Update the draft to confirm it (is_draft = false)
        await updateApplication(updatedApp)
        setApplications((prev) => [updatedApp, ...prev])
        setCurrentDraftId(null)
        trackSavedApplication(updatedApp)
      } else {
        // No draft - create new application (manual add flow)
        const newApp: Application = {
          id: createId(),
          company: editedData.company || extractedData.company || 'N/D',
          companyId: extractedData.companyId || null,
          companyInfo: extractedData.companyInfo || null,
          positionTitle: editedData.positionTitle || extractedData.positionTitle || null,
          sourceUrl: formUrl.trim() || '',
          expectedSalary: null,
          expectedHourly: null,
          targetAmount: editedData.targetAmount ?? null,
          targetPeriod: editedData.targetPeriod ?? 'monthly',
          targetCurrency: editedData.targetCurrency ?? 'PLN',
          salaryRange: editedData.salaryRange || extractedData.salaryRange,
          techStack: editedData.techStack || extractedData.techStack,
          contractType: editedData.contractType || extractedData.contractType || null,
          benefits: editedData.benefits || extractedData.benefits || null,
          workMode: editedData.workMode ?? extractedData.workMode ?? null,
          officeDays: editedData.officeDays ?? extractedData.officeDays ?? null,
          processStages: stages,
          status: formUrl.trim() ? 'saved' : 'applied',
          archived: false,
          note: null,
          followUpAt: null,
          rawMarkdown: extractedData.rawMarkdown || null,
          createdAt: now,
          updatedAt: now,
        }

        await insertApplication(sessionUserId, newApp)
        setApplications((prev) => [newApp, ...prev])

        trackSavedApplication(newApp)
      }

      setFormUrl('')
      setIsEditModalOpen(false)
      setExtractedData(null)
    } catch (err) {
      console.error(err)
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to save data. Please try again.'
      setFormError(message)
    } finally {
      setIsSavingApp(false)
    }
  }

  // Cancel draft - delete the draft application
  const handleCancelDraft = async () => {
    if (currentDraftId) {
      try {
        await deleteApplicationDb(currentDraftId)
      } catch (err) {
        console.error('Failed to delete draft:', err)
      }
      setCurrentDraftId(null)
    }
    setIsEditModalOpen(false)
    setExtractedData(null)
  }

  // Application update handlers
  const toggleStage = (appId: string, stageId: string) => {
    setApplications((prev) =>
      prev.map((app) => {
        if (app.id !== appId || app.archived || AUTO_PROGRESSION_DISABLED_STATUSES.includes(app.status)) return app
        const updatedStages = app.processStages.map((stage) =>
          stage.id === stageId ? { ...stage, completed: !stage.completed } : stage
        )
        const allDone = updatedStages.every((s) => s.completed)
        const updatedApp: Application = {
          ...app,
          processStages: updatedStages,
          status: allDone ? 'offer' : 'in_process',
          updatedAt: new Date().toISOString(),
        }
        updateApplication(updatedApp).catch((err) => console.error(err))
        return updatedApp
      })
    )
  }

  const updateStages = async (appId: string, stages: Application['processStages']) => {
    const target = applications.find((app) => app.id === appId)
    if (!target || target.archived) return
    let updatedApp: Application | null = null

    setApplications((prev) =>
      prev.map((app) => {
        if (app.id !== appId || app.archived) return app

        if (AUTO_PROGRESSION_DISABLED_STATUSES.includes(app.status)) {
          updatedApp = {
            ...app,
            processStages: stages,
            updatedAt: new Date().toISOString(),
          }
          return updatedApp
        }

        const allDone = stages.length > 0 && stages.every((s) => s.completed)
        const nextStatus = stages.length === 0 ? 'applied' : allDone ? 'offer' : 'in_process'

        updatedApp = {
          ...app,
          processStages: stages,
          status: nextStatus,
          updatedAt: new Date().toISOString(),
        }
        return updatedApp
      })
    )

    if (updatedApp) {
      try {
        await updateApplication(updatedApp)
      } catch (err) {
        console.error(err)
      }
    }
  }

  const rejectApplication = (appId: string, reason: RejectReason) => {
    const target = applications.find((a) => a.id === appId && !a.archived)
    if (!target) return

    setApplications((prev) =>
      prev.map((app) =>
        app.id === appId && !app.archived
          ? { ...app, status: 'rejected' as const, rejectReason: reason, updatedAt: new Date().toISOString() }
          : app
      )
    )
    updateApplication({
      ...target,
      status: 'rejected',
      rejectReason: reason,
      updatedAt: new Date().toISOString(),
    }).catch((err) => console.error(err))
    setOpenRejectId(null)
    setRejectState({ code: 'too_expensive' })
  }

  const updateStatus = (appId: string, status: ApplicationStatus, offerReason?: string | null) => {
    const target = applications.find((a) => a.id === appId)
    if (!target) return

    if (!isValidTransition(target.status, status)) {
      console.warn(`Invalid status transition: ${target.status} → ${status}`)
      return
    }

    if (target.status === status) return

    const updatedAt = new Date().toISOString()
    const isOfferOutcome = status === 'offer_declined' || status === 'accepted'
    const updatedApp: Application = {
      ...target,
      status,
      archived: status === 'archived',
      offerDecisionReason: isOfferOutcome ? (offerReason ?? null) : null,
      rejectReason: status === 'rejected' ? target.rejectReason : undefined,
      updatedAt,
    }

    setApplications((prev) => prev.map((app) => (app.id === appId ? updatedApp : app)))
    updateApplication(updatedApp).catch((err) => console.error('Failed to update status:', err))
  }

  const archiveApplication = (appId: string) => {
    const app = applications.find((a) => a.id === appId)
    if (!app) return

    const updatedApp: Application = {
      ...app,
      archived: true,
      status: 'archived',
      updatedAt: new Date().toISOString(),
    }

    setApplications((prev) =>
      prev.map((a) => (a.id === appId ? updatedApp : a))
    )
    
    updateApplication(updatedApp).catch((err) => console.error(err))
    
    if (openRejectId === appId) {
      setOpenRejectId(null)
      setRejectState({ code: 'too_expensive' })
    }
  }

  const unarchiveApplication = (appId: string) => {
    setApplications((prev) =>
      prev.map((app) =>
        app.id === appId
          ? { ...app, archived: false, status: app.status === 'archived' ? 'in_process' : app.status }
          : app
      )
    )
    const app = applications.find((a) => a.id === appId)
    if (app) {
      updateApplication({
        ...app,
        archived: false,
        status: app.status === 'archived' ? 'in_process' : app.status,
        updatedAt: new Date().toISOString(),
      }).catch((err) => console.error(err))
    }
  }

  const updateNote = (appId: string, note: string) => {
    const target = applications.find((a) => a.id === appId)
    if (!target || target.archived) return
    setApplications((prev) =>
      prev.map((app) => (app.id === appId ? { ...app, note, updatedAt: new Date().toISOString() } : app))
    )
    updateApplication({
      ...target,
      note,
      updatedAt: new Date().toISOString(),
    }).catch((err) => console.error(err))
  }

  const updateDetails = (
    appId: string,
    details: { positionTitle?: string | null; contractType?: string | null; benefits?: string | null; techStack?: string[]; workMode?: WorkModeType; officeDays?: number | null }
  ) => {
    const target = applications.find((a) => a.id === appId)
    if (!target || target.archived) return

    const updatedApp = {
      ...target,
      ...(details.positionTitle !== undefined && { positionTitle: details.positionTitle }),
      ...(details.contractType !== undefined && { contractType: details.contractType }),
      ...(details.benefits !== undefined && { benefits: details.benefits }),
      ...(details.techStack !== undefined && { techStack: details.techStack }),
      ...(details.workMode !== undefined && { workMode: details.workMode }),
      ...(details.officeDays !== undefined && { officeDays: details.officeDays }),
      updatedAt: new Date().toISOString(),
    }

    setApplications((prev) => prev.map((app) => (app.id === appId ? updatedApp : app)))
    updateApplication(updatedApp).catch((err) => console.error(err))
  }

  const toggleFavorite = (appId: string) => {
    const target = applications.find((a) => a.id === appId)
    if (!target) return
    const newFavorite = !target.favorite
    setApplications((prev) =>
      prev.map((app) => (app.id === appId ? { ...app, favorite: newFavorite, updatedAt: new Date().toISOString() } : app))
    )
    updateApplication({
      ...target,
      favorite: newFavorite,
      updatedAt: new Date().toISOString(),
    }).catch((err) => console.error(err))
  }

  const updateCompensation = async (
    appId: string,
    payload: {
      salaryRange: SalaryRange
      targetAmount: number | null
      targetPeriod: SalaryPeriod
      targetCurrency: Currency
    }
  ) => {
    const target = applications.find((a) => a.id === appId)
    if (!target || target.archived) return

    setApplications((prev) =>
      prev.map((app) => {
        if (app.id !== appId) return app

        const updatedApp: Application = {
          ...app,
          salaryRange: payload.salaryRange,
          targetAmount: payload.targetAmount,
          targetPeriod: payload.targetPeriod,
          targetCurrency: payload.targetCurrency,
          expectedSalary: null,
          expectedHourly: null,
          updatedAt: new Date().toISOString(),
        }

        updateApplication(updatedApp).catch((err) => console.error(err))
        return updatedApp
      })
    )
  }

  // Settings handlers
  const handleSaveSettings = async (next: UserSettings) => {
    if (!sessionUserId) return
    const prev = settings
    setSettings(next)
    setIsSavingSettings(true)
    setSettingsError(null)
    try {
      await saveUserSettings(sessionUserId, next)
    } catch (err) {
      console.error('saveUserSettings failed', err)
      setSettings(prev)
      setSettingsError('Failed to save settings.')
      throw err
    } finally {
      setIsSavingSettings(false)
    }
  }

  const handleDisplayModeChange = (mode: 'cards' | 'table') => {
    setDisplayMode(mode)
    if (sessionUserId) {
      void handleSaveSettings({ ...settings, defaultDisplayMode: mode })
    }
  }

  const value: ApplicationsContextValue = {
    // Data
    applications,
    settings,
    subscription,
    userPlan,

    // Loading states
    isLoadingApps,
    appsLoadError,
    isLoadingSettings,
    isSavingSettings,
    settingsError,

    // Computed
    activeApps,
    archivedApps,
    effectivePlan,
    isPro,

    // Dashboard UI state
    viewMode,
    companyFilter,
    displayMode,
    sortBy,
    sortKey,
    sortDir,
    statusFilter,
    appsPage,
    totalPages,

    // Computed for dashboard
    visibleApps,
    filteredApps,
    paginatedApps,
    stats,
    metrics,
    sankeyData,
    depthStats,
    statItems,
    analyticsStatItems,
    statusLabels,
    availableStatusFilters,
    displayCurrency,
    currencySymbol,

    // Form state
    formUrl,
    isSubmitting,
    formError,
    extractedData,
    isEditModalOpen,
    isSavingApp,

    // Reject flow
    openRejectId,
    rejectState,

    // Actions - Applications
    handleAddApplication,
    handleManualAdd,
    handleConfirmExtractedData,
    toggleStage,
    updateStages,
    updateStatus,
    rejectApplication,
    archiveApplication,
    unarchiveApplication,
    updateNote,
    updateDetails,
    toggleFavorite,
    updateCompensation,
    checkDuplicate,

    // Actions - Settings
    handleSaveSettings,
    refreshSubscription,

    // Actions - UI
    setViewMode,
    setCompanyFilter,
    setDisplayMode,
    setSortKey,
    setSortDir,
    setStatusFilter,
    setAppsPage,
    setFormUrl,
    setOpenRejectId,
    setRejectState,
    handleCancelDraft,
    handleDisplayModeChange,
  }

  return <ApplicationsContext.Provider value={value}>{children}</ApplicationsContext.Provider>
}
